// React Components
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Ant Design Components
import { Divider, Table } from 'antd';
import { SwapLeftOutlined, SwapOutlined, SwapRightOutlined } from '@ant-design/icons';

// Utilities & Helpers
import { toISTDate } from '@Apis/constants';

// UI Components
import H3Text from '@Uilib/h3Text';
import H3Modal from '@Uilib/H3Modal';
import PRZText from '../../../Common/UI/PRZText';

// Actions
import PurchaseRequestActions from '@Actions/purchaseRequestActions';
import UserActions from '@Actions/userActions';

// Inventory Components
import ViewStockTransfer from '@Components/Inventory/StockTransfer/ViewStockTransfer';

// Styles
import './style.scss';

const PendingStockTransferList = ({
  user,
  getPendingActions,
  getPendingActionsLoading,
  pendingActions,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showSoModal, setShowSoModal] = useState(false);
  const [selectedST, setSelectedST] = useState(null);

  const tableColumns = [
    {
      title: '',
      render: (record) => (
        <div className="mobile-list__card">
          <div className="mobile-list__card_header">
            <div className="mobile-list__card_header-left">
              <PRZText text={`#${record?.stock_transfer_number}`} className="mobile-list__card_item-number" />
            </div>
          </div>
          <div className="mobile-list__card_body">
            <div className="mobile-list__card_body-left" style={{ width: '100%' }}>
              <PRZText
                text={`FROM - ${(record?.source_department_info?.tenant_name ? `${record?.source_department_info?.tenant_name}` : '')} (${(record?.source_department_info ? `${record?.source_department_info?.alias_name}` : '')})`}
                className="mobile-list__card_item-details"
              />
              <PRZText
                text={`TO - ${record?.destination_department_info?.tenant_name ? `${record?.destination_department_info?.tenant_name}` : ''} (${record?.destination_department_info?.alias_name ? `${record?.destination_department_info?.alias_name}` : ''})`}
                className="mobile-list__card_item-details"
              />
              <PRZText
                text={`ITEM(s) - ${record?.no_of_items}`}
                className="mobile-list__card_item-details"
              />
              <PRZText
                text={`CREATED TIME - ${toISTDate(record?.order_date).format('DD/MM/YYYY')} by ${record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown'}`}
                className="mobile-list__card_item-details"
              />
              <PRZText text={`TRANSFER DATE - ${toISTDate(record?.stock_transfer_date).format('DD/MM/YYYY')}`} className="mobile-list__card_item-details" />
            </div>
          </div>
          <Divider className="mobile-list__card_divider" />
          <div className="mobile-list__card_footer">
            <div className="mobile-list__card_footer-left">
              {record?.source_department_info?.tenant_id === record?.destination_department_info?.tenant_id && (
                <div className="st-type-tag" style={{ backgroundColor: '#5287da', marginTop: '5px' }}>
                  <SwapOutlined />
                  <H3Text text="Internal" className="#5287da" />
                </div>
              )}
              {record?.source_department_info?.tenant_id !== record?.destination_department_info?.tenant_id
                && record?.source_department_info?.tenant_id === user?.tenant_info?.tenant_id
                && (
                  <div className="st-type-tag" style={{ backgroundColor: '#f15353', marginTop: '5px' }}>
                    <SwapRightOutlined />
                    <H3Text text="Outgoing" className="" />
                  </div>
                )}
              {record?.source_department_info?.tenant_id !== record?.destination_department_info?.tenant_id
                && record?.destination_department_info?.tenant_id === user?.tenant_info?.tenant_id
                && (
                  <div className="st-type-tag" style={{ backgroundColor: '#2fbc6e', marginTop: '5px' }}>
                    <SwapLeftOutlined />
                    <H3Text text="Incoming" className="" />
                  </div>
                )}
              {record?.source_department_info?.tenant_id !== record?.destination_department_info?.tenant_id
                && record?.destination_department_info?.tenant_id !== user?.tenant_info?.tenant_id
                && record?.source_department_info?.tenant_id !== user?.tenant_info?.tenant_id
                && (
                  <div className="st-type-tag" style={{ backgroundColor: '#5287da', marginTop: '5px' }}>
                    <SwapLeftOutlined />
                    <H3Text text="External" className="" />
                  </div>
                )}
            </div>
          </div>
        </div>
      ),
      responsive: ['xs'],
    },
    {
      title: '#',
      render: (text, record) => (
        <div className="column-number__wrapper">
          <H3Text text={record?.stock_transfer_number} />
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
    },
    {
      title: 'TRANSFER TYPE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div className="transfer-type__wrapper">
          {record?.source_department_info?.tenant_id === record?.destination_department_info?.tenant_id && (
            <div className="st-type-tag" style={{ backgroundColor: '#5287da' }}>
              <SwapOutlined />
              <H3Text text="Internal" className="#5287da" />
            </div>
          )}
          {record?.source_department_info?.tenant_id !== record?.destination_department_info?.tenant_id
            && record?.source_department_info?.tenant_id === user?.tenant_info?.tenant_id
            && (
              <div className="st-type-tag" style={{ backgroundColor: '#f15353' }}>
                <SwapRightOutlined />
                <H3Text text="Outgoing" className="" />
              </div>
            )}
          {record?.source_department_info?.tenant_id !== record?.destination_department_info?.tenant_id
            && record?.destination_department_info?.tenant_id === user?.tenant_info?.tenant_id
            && (
              <div className="st-type-tag" style={{ backgroundColor: '#2fbc6e' }}>
                <SwapLeftOutlined />
                <H3Text text="Incoming" className="" />
              </div>
            )}
          {record?.source_department_info?.tenant_id !== record?.destination_department_info?.tenant_id
            && record?.destination_department_info?.tenant_id !== user?.tenant_info?.tenant_id
            && record?.source_department_info?.tenant_id !== user?.tenant_info?.tenant_id
            && (
              <div className="st-type-tag" style={{ backgroundColor: '#5287da' }}>
                <SwapLeftOutlined />
                <H3Text text="External" className="" />
              </div>
            )}
        </div>
      ),
    },
    {
      title: 'FROM ',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div>
          <H3Text text={(record?.source_department_info?.tenant_name ? `${record?.source_department_info?.tenant_name}` : '')} />
          <H3Text text={(record?.source_department_info ? `${record?.source_department_info?.alias_name}` : '')} className="table-subscript" />
        </div>
      ),
    },
    {
      title: 'TO',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div>
          <H3Text text={(record?.destination_department_info?.tenant_name ? `${record?.destination_department_info?.tenant_name}` : '')} />
          <H3Text text={(record?.destination_department_info?.alias_name ? `${record?.destination_department_info?.alias_name}` : '')} className="table-subscript" />
        </div>
      ),
    },
    {
      title: 'ITEM(s)',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (text, record) => (
        <div>
          {record?.no_of_items}
        </div>
      ),
    },
    {
      title: 'CREATED TIME',
      render: (record) => (
        <div>
          <div className="column-date">
            {toISTDate(record?.order_date).format('DD/MM/YYYY')}
          </div>
          <H3Text text={(record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown')} className="table-subscript" />
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
    },
    {
      title: 'TRANSFER DATE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div className="column-date">
          {toISTDate(record?.stock_transfer_date).format('DD/MM/YYYY')}
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      fixed: 'right',
      render: (record) => (
        <div className="list__actions">
          <div className="list__actions-button list__actions-button-dark" onClick={() => { setSelectedST(record); setShowSoModal(true); }}>
            View
          </div>
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
      className: 'list__actions-column'
    },
  ];

  useEffect(() => {
    if (user) {
      getPendingActions('map_st', 10, currentPage, () => { });
    }
  }, [user, getPendingActions, currentPage]);
  return (
    <React.Fragment>
      <div className="doc-list__wrapper">
        <div className="ant-row">
          <div className="ant-col-md-24 ant-col-xs-24">
            <div className="doc-list__table-wrapper">
              <Table
                title={() => (
                  <div className="doc-list__table-header">
                    <div className="doc-list__table-header__left">
                      {`${Number(pendingActions?.st_approval_count)} Stock Transfer`}
                    </div>
                  </div>
                )}
                loading={getPendingActionsLoading}
                showHeader
                size="small"
                scroll="scroll"
                columns={tableColumns}
                rowKey="stock_transfer_id"
                dataSource={pendingActions?.stock_transfers || []}
                pagination={(Number(pendingActions?.st_approval_count) > 10) ? {
                  defaultCurrent: 1,
                  current: currentPage,
                  total: Number(pendingActions?.st_approval_count),
                  pageSize: 10,
                  onChange: (page) => {
                    getPendingActions('map_st', 10, page, () => { });
                    setCurrentPage(page);
                  },
                  position: ['bottomRight'],
                  showSizeChanger: false,
                } : false}
              />
            </div>
          </div>
        </div>
      </div>
      <H3Modal
        onClose={() => {
          getPendingActions('map_st', 10, currentPage, () => { });
          setShowSoModal(false);
        }}
        className="pending-st__view-st"
        isOpen={showSoModal}
        title={`Stock Transfer #${selectedST ? selectedST?.stock_transfer_number : ''} for ${selectedST?.source_department_info?.tenant_name}`}
        showHeader
      >
        <ViewStockTransfer
          selectedSTId={selectedST?.stock_transfer_id}
          isQuickView
        />
      </H3Modal>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  UserReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getPendingActionsLoading: UserReducers.getPendingActionsLoading,
  pendingActions: UserReducers.pendingActions,
});

const mapDispatchToProps = (dispatch) => ({
  getPendingActions: (actionType, limit, page, callback) => dispatch(UserActions.getPendingActions(actionType, limit, page, callback)),
  getPurchaseRequestsSuccess: (purchaseRequests) => dispatch(PurchaseRequestActions.getPurchaseRequestsSuccess(purchaseRequests)),
});

PendingStockTransferList.propTypes = {
  user: PropTypes.any,
  getPendingActions: PropTypes.func,
  getPendingActionsLoading: PropTypes.bool,
  pendingActions: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(PendingStockTransferList);
